import { useCallback, useMemo } from 'react';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe/features/permissions';

import { useTelemetry } from '@order/Telemetry';

import { useLocationPermissionPrimerEnabled } from '../useLocationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the location permission primer flow at app boot. Instantiates the
 * permissions actor (with telemetry handlers wired into the machine's
 * external action slots) and exposes the flags + handlers the consumer needs
 * to decide whether to render a loading screen, the primer, or its normal
 * content.
 */
export const useLocationPermissionPrimer = () => {
  const { track } = useTelemetry();

  // Variation `A` shows the primer during app onboarding. `B` (location
  // search tab) and `C` (post-order-confirmation) are reserved for now.
  const { isLocationPrimerEnabled, locationPrimerVariation } = useLocationPermissionPrimerEnabled();

  // ─── Telemetry Actions ───────────────────────────────────────────────

  const onLocationPrimerShow = useCallback(() => {
    track('location_primer_shown', {
      entry_point: 'onboarding',
      variant: locationPrimerVariation,
    });
  }, [track, locationPrimerVariation]);

  const onRequestLocationPermission = useCallback(() => {
    track('location_primer_cta_tapped', {
      entry_point: 'onboarding',
      variant: locationPrimerVariation,
    });
  }, [track, locationPrimerVariation]);

  const onSkipLocationPermission = useCallback(() => {
    track('location_primer_dismissed', {
      entry_point: 'onboarding',
      variant: locationPrimerVariation,
    });
  }, [track, locationPrimerVariation]);

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: { onLocationPrimerShow, onRequestLocationPermission, onSkipLocationPermission },
    }),
    [onLocationPrimerShow, onRequestLocationPermission, onSkipLocationPermission],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isLocationPrimerEnabled,
    implementations,
  });

  const {
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    requestLocationPermissions,
    skipLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isLocationPrimerVisible || isRequestingLocationPermission || isSkippingLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLocationPermissionsLoading,
    isPrimerActive,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    onAllow: requestLocationPermissions,
    onSkip: skipLocationPermissions,
  };
};
